import { Hit } from 'algoliasearch/lite';
import {
  AppointmentDepositPlacement,
  Appointments,
  AppointmentsSchedulingType,
  DepositPaymentStatus,
  PaymentMethod,
  Users,
} from '@prisma/client';
import * as yup from 'yup';
import { SORTABLE_KEYS } from '../../constants/appointments';

const ALGOLIA_FIELDS = [
  'tattooLocation',
  'tattooDetails',
  'tattooSize',
  'tattooColor',
  'isFirstTattoo',
  'isNewCustomer',
  'budget',
  'priceType',
  'price',
  'minPrice',
  'maxPrice',
  'hourlyPrice',
  'depositPrice',
  'rescheduledByCust',
  'rescheduledByEmployee',
  'refImgsId',
  'createdAt',
  'updatedAt',
  'startDateTime',
  'endDateTime',
  'tripCity',
  'timezone',
  'liabilityWaivedOn',
  'businessId',
  'checkoutSessionCompleted',
  'checkoutSessionActive',
  'tipAmt',
  'appointmentType',
  'serviceCategory',
  'serviceId',
  'status',
] as const satisfies (keyof Appointments)[];

export type CheckoutPaymentMethod =
  | 'CREDIT'
  | 'TERMINAL'
  | 'MANUAL'
  | 'CASH'
  | 'GIFT_CARD'
  | 'FREE';

export interface CustomCheckoutItem {
  itemId?: string | null;
  id: string;
  name: string;
  price: number;
  quantity: number;
}

export interface CheckoutFormValues {
  customer: {
    customerId: string;
    firstName: string;
    lastName: string | undefined;
    email: string | undefined;
    phoneNumber: string;
    dob?: string;
  };
  employeeId: string;
  appointmentId: string;
  startDateTime: string;
  price: number;
  tipAmt: number;
  isCashTaxed: boolean;
  paymentMethod: CheckoutPaymentMethod;
  customOrderItems?: CustomCheckoutItem[];
  includeBonus?: boolean;
  failedOrderId?: string;
  paymentIntentId?: string;
  discountId?: string;
  serviceId?: number;
  businessTripId?: string;
}

export interface MarkDepositPaidPayload {
  depositPaymentMethod: PaymentMethod;
}

export type SortableKeys = (typeof SORTABLE_KEYS)[number];

export const tipSchema = yup.object({
  tipAmt: yup
    .number()
    .min(0, 'Tip amount must be greater than or equal to 0')
    .nullable()
    .optional(),
});

export type TipState = yup.InferType<typeof tipSchema>;

export type AppointmentHit = Hit<
  {
    firstName: Users['firstName'];
    lastName: Users['lastName'];
    phoneNumber: Users['phoneNumber'];
    fullName: string;
    recordUpdatedAt: number;
  } & Pick<Appointments, (typeof ALGOLIA_FIELDS)[number]>
>;

const whenMultipleSessions = (requiredMessage: string) => ({
  is: (numberOfSessions: number) => numberOfSessions > 1,
  then: (schema: any) => schema.required(requiredMessage),
  otherwise: (schema: any) => schema.optional(),
});

export const acceptFormSchema = yup.object().shape({
  artist: yup.string().required('Tattoo artist is required'),
  appointmentLengthInMinutes: yup
    .number()
    .required('Please select an appointment length'),
  numberOfSessions: yup
    .number()
    .integer('Number of sessions must be an integer')
    .min(1, 'Number of sessions must be greater than 0')
    .required('Please select a number of sessions'),
  schedulingType: yup
    .mixed<AppointmentsSchedulingType>()
    .oneOf(
      Object.values(AppointmentsSchedulingType),
      'Please select a scheduling type',
    )
    .when(
      'numberOfSessions',
      whenMultipleSessions('Please select a scheduling type'),
    ),
  bufferDays: yup
    .number()
    .integer('Buffer days must be an integer')
    .min(0)
    .optional(),
  depositPlacement: yup
    .mixed<AppointmentDepositPlacement>()
    .oneOf(
      Object.values(AppointmentDepositPlacement),
      'Please select a deposit placement',
    )
    .when(
      'numberOfSessions',
      whenMultipleSessions('Please select a deposit placement'),
    ),
  deposit: yup.boolean().required('Do you want to collect a deposit?'),
  depositAmount: yup.number().when('deposit', {
    is: (deposit: boolean) => deposit,
    then: (schema: any) =>
      schema
        .min(0.01, 'Deposit amount must be greater than 0')
        .required('Deposit amount is required'),
    otherwise: (schema: any) => schema.optional(),
  }),
  priceType: yup.string().required('Please select a price type'),
  minPrice: yup.number().optional().min(0),
  maxPrice: yup
    .number()
    .optional()
    .min(0)
    .test(
      'max-greater-than-min',
      'Max price must be greater than min price',
      // eslint-disable-next-line func-names
      function (value) {
        const { minPrice } = this.parent;
        if (!minPrice || !value) return true;
        return value > minPrice;
      },
    ),
  hourlyPrice: yup.number().optional().min(0),
  setPrice: yup
    .number()
    .min(0)
    .test(
      'set-price-greater-than-deposit',
      'Set price must be greater than or equal to deposit amount',
      // eslint-disable-next-line func-names
      function (value) {
        const { depositAmount, priceType } = this.parent;
        if (priceType && priceType !== 'FIXED') return true;
        return (value || 0) >= (depositAmount || 0);
      },
    ),
  customDesignMessage: yup.string().optional(),
});

export type AcceptFormState = yup.InferType<typeof acceptFormSchema>;

export const durationFormSchema = yup.object().shape({
  appointmentLengthInMinutes: yup
    .number()
    .integer('Appointment length must be an integer')
    .min(1, 'Appointment length must be greater than 0')
    .required('Please select a duration'),
});

export type DurationFormState = yup.InferType<typeof durationFormSchema>;

export interface TakenSessionSlot {
  startDateTime: string;
  endDateTime: string;
}

export const appointmentRefundDepositSchema = yup.object({
  depositStatus: yup
    .mixed<DepositPaymentStatus>()
    .oneOf(Object.values(DepositPaymentStatus), 'Please select an option')
    .required('Please select an option'),
});

export type AppointmentRefundDepositState = yup.InferType<
  typeof appointmentRefundDepositSchema
>;

export const appointmentTransferDepositSchema = yup.object({
  fromAppointmentId: yup
    .string()
    .required('Please select an appointment to transfer from'),
  toAppointmentId: yup
    .string()
    .required('Please select an appointment to transfer to'),
});

export type AppointmentTransferDepositState = yup.InferType<
  typeof appointmentTransferDepositSchema
>;

export const finalTransferAppointmentDepositSchema = yup.object({
  fromAppointmentId: yup.string().required('Please select an appointment'),
  toAppointmentId: yup.string().required('Please select an appointment'),
  businessId: yup.string().required('Please refresh the page'),
  customerId: yup.string().required('Please refresh the page'),
});

export type TransferAppointmentDepositPayload = yup.InferType<
  typeof finalTransferAppointmentDepositSchema
>;

export interface AppointmentDepositCheckoutSession {
  isPaymentRequired: boolean;
  depositCents: number;
  serviceFeeCents: number;
  salesTaxCents: number;
  isDepositPaid: boolean;
  serviceFeeRate: number;
  businessTaxRate: number;
  totalPriceCents: number;
  paymentIntent:
    | {
        id: string;
        client_secret: string;
        paymentId: string;
      }
    | undefined
    | null;
  paymentMethod: PaymentMethod;
  appointmentId: string;
}
